import { useState } from 'react'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Plus, Trash2 } from 'lucide-react'
import { RevenueShareMode, RevenueShareTier } from '@/types'

interface TieredRevenueShareConfigProps {
  value: {
    driverRevenueShare: number       // Single rate percentage (0-100)
    revenueShareMode?: RevenueShareMode
    revenueShareTiers?: RevenueShareTier[]
  }
  onChange: (field: string, value: number | string | RevenueShareTier[] | undefined) => void
  disabled?: boolean
  rawInputs?: {
    driverRevenueShare: string
  }
  onRawInputChange?: (field: string, value: string) => void
  onDriverShareBlur?: () => void
  includeVacationInShare?: boolean
  displayLabel?: string
}

/**
 * Generate a deterministic ID for a tier based on its threshold.
 * Since thresholds must be unique, this provides stable React keys.
 */
function getTierId(threshold: number): string {
  return `tier-${threshold}`
}

export function TieredRevenueShareConfig({
  value,
  onChange,
  disabled = false,
  rawInputs,
  onRawInputChange,
  onDriverShareBlur,
  includeVacationInShare = false,
  displayLabel
}: TieredRevenueShareConfigProps) {
  // Local state for input fields to allow empty values during editing
  // Keyed by threshold (the stable identifier)
  const [tierInputs, setTierInputs] = useState<Record<number, { threshold: string; rate: string }>>({})

  // Derive tiers directly from props - this is the source of truth
  const tiers = value.revenueShareTiers ?? []
  const sortedTiers = [...tiers].sort((a, b) => a.threshold - b.threshold)

  const displayDriverShare = rawInputs ? rawInputs.driverRevenueShare : value.driverRevenueShare.toString()

  const handleModeChange = (mode: RevenueShareMode) => {
    onChange('revenueShareMode', mode)

    if (mode === 'tiered' && tiers.length === 0) {
      // Initialize with base tier using current driver share rate
      const baseTier: RevenueShareTier = { threshold: 0, rate: value.driverRevenueShare }
      onChange('revenueShareTiers', [baseTier])
    }
  }

  const handleTierInputChange = (originalThreshold: number, field: 'threshold' | 'rate', inputValue: string) => {
    setTierInputs(prev => ({
      ...prev,
      [originalThreshold]: {
        threshold: field === 'threshold' ? inputValue : (prev[originalThreshold]?.threshold ?? originalThreshold.toString()),
        rate: field === 'rate' ? inputValue : (prev[originalThreshold]?.rate ?? tiers.find(t => t.threshold === originalThreshold)?.rate.toString() ?? '')
      }
    }))
  }

  const handleTierBlur = (originalThreshold: number, field: 'threshold' | 'rate') => {
    const input = tierInputs[originalThreshold]?.[field]
    const tier = tiers.find(t => t.threshold === originalThreshold)
    if (!tier) return

    const currentValue = field === 'threshold' ? tier.threshold : tier.rate
    const inputToParse = input ?? currentValue.toString()

    const defaultValue = field === 'threshold' ? 0 : 1
    const numericValue = inputToParse === '' ? defaultValue : parseFloat(inputToParse)
    if (isNaN(numericValue)) return

    // Don't allow non-base tiers to have threshold 0
    const isNonBaseTierWithZeroThreshold = field === 'threshold' && numericValue === 0 && originalThreshold !== 0
    if (isNonBaseTierWithZeroThreshold) {
      // Reset input to original value
      setTierInputs(prev => ({
        ...prev,
        [originalThreshold]: { ...prev[originalThreshold], threshold: originalThreshold.toString() }
      }))
      return
    }

    // Check for duplicate thresholds when changing threshold
    if (field === 'threshold' && numericValue !== originalThreshold) {
      const wouldBeDuplicate = tiers.some(t => t.threshold === numericValue && t.threshold !== originalThreshold)
      if (wouldBeDuplicate) {
        // Reset input to original value
        setTierInputs(prev => ({
          ...prev,
          [originalThreshold]: { ...prev[originalThreshold], threshold: originalThreshold.toString() }
        }))
        return
      }
    }

    // Update the tier
    const updatedTiers = tiers.map(t =>
      t.threshold === originalThreshold ? { ...t, [field]: numericValue } : t
    )

    // Clear local input state for this tier since we're committing the change
    // If threshold changed, we need to migrate the input state
    if (field === 'threshold' && numericValue !== originalThreshold) {
      setTierInputs(prev => {
        const { [originalThreshold]: _, ...rest } = prev
        return rest
      })
    }

    onChange('revenueShareTiers', updatedTiers)
  }

  const handleAddTier = () => {
    const maxThreshold = Math.max(...tiers.map(t => t.threshold), 0)
    const suggestedThreshold = maxThreshold + 10000
    const lastTier = sortedTiers[sortedTiers.length - 1]
    const newRate = Math.min((lastTier?.rate ?? value.driverRevenueShare) + 5, 100)

    const newTier: RevenueShareTier = { threshold: suggestedThreshold, rate: newRate }
    onChange('revenueShareTiers', [...tiers, newTier])
  }

  const handleRemoveTier = (threshold: number) => {
    if (threshold === 0) return // Can't remove base tier

    const updatedTiers = tiers.filter(t => t.threshold !== threshold)
    // Clean up local input state
    setTierInputs(prev => {
      const { [threshold]: _, ...rest } = prev
      return rest
    })
    onChange('revenueShareTiers', updatedTiers)
  }

  // Validation helpers
  const isBaseTier = (threshold: number) => threshold === 0
  const hasBaseTier = tiers.some(t => t.threshold === 0)
  const hasDuplicateThresholds = new Set(tiers.map(t => t.threshold)).size !== tiers.length

  // Get display value for input (local state if editing, otherwise from tier)
  const getInputValue = (tier: RevenueShareTier, field: 'threshold' | 'rate'): string => {
    const localInput = tierInputs[tier.threshold]
    if (localInput && localInput[field] !== undefined) {
      return localInput[field]
    }
    return tier[field].toString()
  }

  return (
    <div className="grid gap-3">
      {/* Mode Selection */}
      <div className="space-y-1.5">
        <Label className="font-medium">Fördelningsmodell</Label>
        <RadioGroup
          value={value.revenueShareMode ?? 'single'}
          onValueChange={(val: RevenueShareMode) => handleModeChange(val)}
          className="flex space-x-6"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="single" id="share-single" disabled={disabled} />
            <Label htmlFor="share-single" className="text-sm font-normal cursor-pointer">
              Enkel procentsats
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="tiered" id="share-tiered" disabled={disabled} />
            <Label htmlFor="share-tiered" className="text-sm font-normal cursor-pointer">
              Nivåbaserad
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Single Rate Mode */}
      {(value.revenueShareMode ?? 'single') === 'single' && (
        <div className="space-y-1.5">
          <Label htmlFor="driverShare" className="font-medium">
            {displayLabel || (includeVacationInShare ? 'Förarandel (inkl. semester) (%)' : 'Förarandel (%)')}
          </Label>
          <Input
            id="driverShare"
            type="number"
            value={displayDriverShare}
            onChange={(e) => {
              if (onRawInputChange) {
                onRawInputChange('driverRevenueShare', e.target.value)
              }
              const val = e.target.value === '' ? 0 : parseFloat(e.target.value)
              onChange('driverRevenueShare', val)
            }}
            onBlur={onDriverShareBlur}
            min="0"
            max="100"
            step="0.1"
            disabled={disabled}
          />
        </div>
      )}

      {/* Tiered Rate Mode */}
      {value.revenueShareMode === 'tiered' && (
        <div className="space-y-3 p-3 bg-muted/20 rounded-lg border border-border/50">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Nivåer</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddTier}
              disabled={disabled}
              className="gap-1 h-7 text-xs"
            >
              <Plus className="h-3 w-3" />
              Lägg till nivå
            </Button>
          </div>

          {/* Validation warnings */}
          {!hasBaseTier && (
            <p className="text-xs text-destructive">En basnivå med tröskel 0 kr krävs</p>
          )}
          {hasDuplicateThresholds && (
            <p className="text-xs text-destructive">Alla tröskelvärden måste vara unika</p>
          )}

          {/* Tier list */}
          <div className="space-y-2">
            {sortedTiers.map((tier) => (
              <div
                key={getTierId(tier.threshold)}
                className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end"
              >
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">
                    {isBaseTier(tier.threshold) ? 'Bas (alltid)' : 'Från (kr)'}
                  </Label>
                  <Input
                    type="number"
                    value={getInputValue(tier, 'threshold')}
                    onChange={(e) => handleTierInputChange(tier.threshold, 'threshold', e.target.value)}
                    onBlur={() => handleTierBlur(tier.threshold, 'threshold')}
                    min="0"
                    step="1000"
                    disabled={disabled || isBaseTier(tier.threshold)}
                    placeholder="0"
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Andel (%)</Label>
                  <Input
                    type="number"
                    value={getInputValue(tier, 'rate')}
                    onChange={(e) => handleTierInputChange(tier.threshold, 'rate', e.target.value)}
                    onBlur={() => handleTierBlur(tier.threshold, 'rate')}
                    min="1"
                    max="100"
                    step="0.5"
                    disabled={disabled}
                    placeholder="40"
                    className="h-8 text-sm"
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveTier(tier.threshold)}
                  disabled={disabled || isBaseTier(tier.threshold)}
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                  title={isBaseTier(tier.threshold) ? 'Basnivån kan inte tas bort' : 'Ta bort nivå'}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>

          {/* Explanation */}
          <p className="text-xs text-muted-foreground">
            När total nettointäkt överstiger en tröskel används den nivåns andel för <em>hela</em> intäkten.
          </p>
        </div>
      )}
    </div>
  )
}
